/**
 * File Detail View — displays full details for a single client file.
 *
 * Shows the Client File Brief (Summary, §19), voice note player, attachments,
 * SLA timer and context-aware status actions; the extracted cards and modals
 * are mapped in CLAUDE.md §8.4.
 * Both Marinus and Wouter use this view to manage files.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Clock, Pause, CheckCircle2,
  AlertTriangle, Lock,
  Layers, Pencil, AlertOctagon, X,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { getFileById, updateFile, getAllFiles, deleteFile } from '../api/filesApi';
import { createSystemNote, NOTE_BODY_MAX } from '../api/fileNotesApi';
import { getFirstName } from '../auth/authConfig';
import {
  drawFile,
  pauseFile,
  resumeFile,
  sendForSignature,
  markSigned,
  markCompleted,
  pauseAndPromote,
  sendForSignatureAndPromote,
} from '../services/pipelineService';
import { planNextFileAction } from '../utils/pipelineInvariants';
import { isSealedInInbox } from '../utils/inboxDrawGate';
import PickNextFileModal from '../components/files/PickNextFileModal';
import { deleteAllFileStorage } from '../api/storageApi';
import { manualPurge } from '../api/purgeAttachmentsApi';
import { getCaseScreenshotsForFile, updateCaseStructure } from '../api/caseScreenshotsApi';
import { getLatestEventForFile } from '../api/verificationEventsApi';
import { formatDate, formatDateTime, timeAgo } from '../utils/dateHelpers';
import { returnToDashboard, movedToMessage } from '../utils/returnToDashboard';
// SLA helpers are now consumed inside pipelineService — file-level uses
// rely on the live useSLATimer() hook below.
import useSLATimer from '../hooks/useSLATimer';
import useConcurrencyRules from '../hooks/useConcurrencyRules';
import useHandoverTransition from '../hooks/useHandoverTransition';
import useCaseScreenshots from '../hooks/useCaseScreenshots';
import { StatusChangeAnimation } from '../components/gamification/Celebrations';
import { getSLAMotivationalMessage } from '../api/slaMonitor';
import VideoInterstitial from '../components/shared/VideoInterstitial';
import FileNotes from '../components/files/FileNotes';
import { defaultCommissionSplits } from '../components/files/CommissionSplitEditor';
import CommissionSplitsCard from '../components/files/CommissionSplitsCard';
import AttachmentsCard, { PurgeConfirmModal } from '../components/files/AttachmentsCard';
import DeleteFileModal from '../components/files/DeleteFileModal';
import FileActionsCard from '../components/files/FileActionsCard';
import { sharedStyles } from '../components/files/FileDetail.styles';
import StatusTransitionToNbModal from '../components/files/StatusTransitionToNbModal';
import CaseScreenshotsSection from '../components/files/caseScreenshots/CaseScreenshotsSection';
import EditCasesModal, { styles as caseModalStyles } from '../components/files/caseScreenshots/EditCasesModal';
import OverrideUploadModal from '../components/files/caseScreenshots/OverrideUploadModal';
import FlagDetailModal from '../components/files/caseScreenshots/FlagDetailModal';
import AggregateDiagnosticsModal from '../components/files/caseScreenshots/AggregateDiagnosticsModal';
import TimelineItem, { styles as timelineStyles } from '../components/files/TimelineItem';
import WhatsAppPanel from '../components/files/WhatsAppPanel';

const FIRST_FILE_VIDEO_URL =
  'https://yaivpbnutpmmcnrtuoiy.supabase.co/storage/v1/object/public/inbox-videos/Obtaining%20File%201.mp4';

// Status display labels. Keys are the INTERNAL status values stored in the
// database (never renamed); the values are DISPLAY text only — 'submitted'
// renders as "Inbox" (CLAUDE.md §7.1).
const STATUS_LABELS = {
  submitted: 'Inbox',
  in_progress: 'In Progress',
  paused: 'Paused',
  sent_to_sign: 'Awaiting Signature',
  signed: 'Signed & Sent to New Business',
  completed: 'Completed',
};

export default function FileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [file, setFile] = useState(null);
  const [allFiles, setAllFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pauseReason, setPauseReason] = useState('');
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [concurrencyWarning, setConcurrencyWarning] = useState(null);
  const [warningFading, setWarningFading] = useState(false);
  const [statusAnim, setStatusAnim] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Adviser-only manual attachment purge (CLAUDE.md §29).
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const [screenshotWarning, setScreenshotWarning] = useState(null);
  const [screenshotWarningFading, setScreenshotWarningFading] = useState(false);
  const [highlightUpload, setHighlightUpload] = useState(false);
  const [showUrgentConfirm, setShowUrgentConfirm] = useState(false);
  const [urgentToast, setUrgentToast] = useState(null);
  const [urgentToastFading, setUrgentToastFading] = useState(false);
  const [showFirstFileVideo, setShowFirstFileVideo] = useState(false);
  // Return-to-dashboard (CLAUDE.md §39) deferred behind the first-draw
  // celebration video: the navigation args wait here until the interstitial
  // completes (or errors out through its Continue button), so the video is
  // never skipped and a failed video never strands the page.
  const pendingReturnRef = useRef(null);
  // Pick-next-file modal state (CLAUDE.md §7.6). Opens when the user
  // moves the In Progress file out (pause or sent_to_sign) AND other
  // paused files exist at that moment. The outbound action has NOT
  // committed yet — cancel is a true revert.
  const [showPickNextModal, setShowPickNextModal] = useState(false);
  const [pickNextPausedCandidates, setPickNextPausedCandidates] = useState([]);
  const [pickNextSubmittedCandidates, setPickNextSubmittedCandidates] = useState([]);
  // 'pause' | 'send_for_signature' — which outbound action triggered
  // the modal, so the picker knows which atomic helper to call.
  const [pendingOutboundAction, setPendingOutboundAction] = useState(null);
  const [pendingPauseReason, setPendingPauseReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectToast, setRejectToast] = useState(null);
  const [rejectToastFading, setRejectToastFading] = useState(false);
  // Bumped after a status-change writes new system notes, so the
  // FileNotes component can refetch and display them.
  const [notesRefresh, setNotesRefresh] = useState(0);
  const [showEditCasesModal, setShowEditCasesModal] = useState(false);
  // Case-screenshot cluster (CLAUDE.md §8.4, step F4): the per-case rows,
  // per-row upload state, lightbox, flag / override modal state and the
  // Adviser's verification diagnostic live in useCaseScreenshots. loadFile
  // below writes the rows and the diagnostic through its two exposed
  // setters. showEditCasesModal and handleApplyCaseStructure stay here:
  // that write goes through loadFile and changes caseCount / caseLabels,
  // which the memo, the Cases card and the summary read.
  const screenshots = useCaseScreenshots({ file, id, user, setFile, setError, setActionLoading });
  const { setCaseScreenshots, setVerificationDiagnostic } = screenshots;

  // Live SLA timer
  const sla = useSLATimer(file);

  // Concurrency rules
  const concurrency = useConcurrencyRules(allFiles, user?.email, role);

  const loadFile = useCallback(async () => {
    try {
      setLoading(true);
      const [data, all] = await Promise.all([
        getFileById(id),
        getAllFiles(),
      ]);
      setFile(data);
      setAllFiles(all);
      // Multi-case files carry their per-case screenshots in a separate
      // table. Fetch alongside the file row so the upload UI is ready
      // immediately. Legacy files (caseCount == null) skip this.
      if (data?.caseCount != null) {
        const shots = await getCaseScreenshotsForFile(data.id);
        setCaseScreenshots(shots);
      } else {
        setCaseScreenshots([]);
      }
      // Advisor-only: surface any recent Anthropic verification failure
      // for this file. The RLS policy returns nothing for assistants, so
      // a role-gated fetch keeps the call cheap and harmless.
      if (role === 'adviser' && data?.id) {
        try {
          const evt = await getLatestEventForFile(data.id);
          setVerificationDiagnostic(evt || null);
        } catch (e) {
          setVerificationDiagnostic(null);
        }
      } else {
        setVerificationDiagnostic(null);
      }
    } catch (err) {
      setError('Failed to load file');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, role, setCaseScreenshots, setVerificationDiagnostic]);

  useEffect(() => {
    loadFile();
  }, [loadFile]);

  // Auto-dismiss screenshot warning: 4.5s display + 0.5s fade (5s total)
  useEffect(() => {
    if (screenshotWarning) {
      setScreenshotWarningFading(false);
      const fadeTimer = setTimeout(() => setScreenshotWarningFading(true), 4500);
      const removeTimer = setTimeout(() => {
        setScreenshotWarning(null);
        setHighlightUpload(false);
      }, 5000);
      return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
    }
  }, [screenshotWarning]);

  // Auto-dismiss urgent toast: 3s display + 0.5s fade
  useEffect(() => {
    if (urgentToast) {
      setUrgentToastFading(false);
      const fadeTimer = setTimeout(() => setUrgentToastFading(true), 3000);
      const removeTimer = setTimeout(() => setUrgentToast(null), 3500);
      return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
    }
  }, [urgentToast]);

  // Auto-dismiss reject toast: 3.5s display + 0.5s fade
  useEffect(() => {
    if (rejectToast) {
      setRejectToastFading(false);
      const fadeTimer = setTimeout(() => setRejectToastFading(true), 3500);
      const removeTimer = setTimeout(() => setRejectToast(null), 4000);
      return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
    }
  }, [rejectToast]);

  // Auto-dismiss concurrency warning: 3s display + 0.5s fade
  useEffect(() => {
    if (concurrencyWarning) {
      setWarningFading(false);
      const fadeTimer = setTimeout(() => setWarningFading(true), 3000);
      const removeTimer = setTimeout(() => setConcurrencyWarning(null), 3500);
      return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
    }
  }, [concurrencyWarning]);

  // Status transition actions.
  //
  // All transitions delegate to the pipeline service (CLAUDE.md §7.6),
  // the single chokepoint that writes file.status + the matching
  // system note. The Pause and Sent-to-Sign flows are special:
  // they consult planNextFileAction(file, allFiles, …) and, if other
  // paused files exist, open the PickNextFileModal so the outbound +
  // inbound transitions commit atomically.
  const handleStatusChange = async (newStatus) => {
    try {
      setActionLoading(true);
      const currentFirstName = getFirstName(user?.email);
      const actor = { email: user?.email, id: user?.id, firstName: currentFirstName, role };

      let updated = file;
      // True when the first-draw celebration video will play: the return to
      // the dashboard then waits for the interstitial (§39).
      let deferReturn = false;

      switch (newStatus) {
        case 'in_progress':
          if (file.status === 'submitted') {
            updated = await drawFile(file, actor);
            // First-draw-of-the-month celebration video (assistant only).
            // Test records (CLAUDE.md §33) never trigger the celebration
            // and never count as the month's first draw.
            if (role === 'assistant' && !file.isTest) {
              const now = new Date();
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();
              const hasDrawnThisMonth = allFiles.some((f) =>
                !f.isTest &&
                f.drawnBy === user?.email &&
                f.id !== file.id &&
                f.drawnAt &&
                new Date(f.drawnAt).getMonth() === currentMonth &&
                new Date(f.drawnAt).getFullYear() === currentYear
              );
              if (!hasDrawnThisMonth) {
                setShowFirstFileVideo(true);
                deferReturn = true;
              }
            }
          } else if (file.status === 'paused') {
            // Manual resume requires an empty In Progress slot — the
            // Resume button is disabled when canDrawFile is false, so
            // we should never get here with another active file.
            updated = await resumeFile(file, actor);
          }
          break;

        case 'paused': {
          // Test records (CLAUDE.md §33) are released from the In Progress
          // rules: pause commits directly — no forced-pick modal, no
          // "blocked" state. The pause itself (status write + system note
          // + reason note) uses the exact same pipelineService code path.
          if (file.isTest) {
            updated = await pauseFile(file, (pauseReason || '').trim(), actor);
            break;
          }
          const plan = planNextFileAction(file, allFiles, { action: 'pause' });
          if (plan.mode === 'blocked') {
            // Nothing to take over. Active file must stay In Progress —
            // surface the planner's message and abort without writing.
            setConcurrencyWarning(plan.message);
            setShowPauseModal(false);
            setPauseReason('');
            return;
          }
          // plan.mode === 'pick' — at least one candidate exists; the
          // user must pick (even with a single candidate). Defer the
          // actual pause until the modal commits, so cancelling is a
          // true no-op.
          setPendingPauseReason((pauseReason || '').trim());
          setPendingOutboundAction('pause');
          setPickNextPausedCandidates(plan.pausedCandidates);
          setPickNextSubmittedCandidates(plan.submittedCandidates);
          setShowPauseModal(false);
          setPauseReason('');
          setShowPickNextModal(true);
          return;
        }

        case 'sent_to_sign': {
          // Test records commit the send directly — no forced-pick modal
          // (CLAUDE.md §33). Same pipelineService path (system note +
          // attachment purge) as a real send.
          if (file.isTest) {
            updated = await sendForSignature(file, actor);
            break;
          }
          const plan = planNextFileAction(file, allFiles, {
            action: 'send_for_signature',
          });
          if (plan.mode === 'pick') {
            setPendingOutboundAction('send_for_signature');
            setPickNextPausedCandidates(plan.pausedCandidates);
            setPickNextSubmittedCandidates(plan.submittedCandidates);
            setShowPickNextModal(true);
            return;
          }
          // plan.mode === 'commit' — Paused is empty, so SfS may
          // legitimately leave In Progress empty.
          updated = await sendForSignature(file, actor);
          break;
        }

        case 'signed':
          updated = await markSigned(file, actor);
          break;

        case 'completed':
          updated = await markCompleted(file, actor);
          break;

        default:
          break;
      }

      setFile(updated);
      setNotesRefresh((n) => n + 1);

      setShowPauseModal(false);
      setPauseReason('');
      setStatusAnim(newStatus);

      // Refresh allFiles so the dashboard cache is current and downstream
      // gating (concurrency rules, banner) uses fresh state on next render.
      try {
        const refreshed = await getAllFiles();
        setAllFiles(refreshed);
      } catch (e) {
        // Non-critical; the dashboard view will refetch on next mount.
      }

      // Column changed and every write above succeeded: return to the
      // dashboard with this card highlighted in its new column (§39). The
      // early `return`s above (blocked pause, pick-next modal) never reach
      // here because nothing has been written yet.
      const returnArgs = {
        role,
        cardType: 'file',
        cardId: file.id,
        message: movedToMessage(STATUS_LABELS[newStatus] || newStatus),
      };
      if (deferReturn) {
        pendingReturnRef.current = returnArgs;
      } else {
        returnToDashboard(navigate, returnArgs);
      }
    } catch (err) {
      console.error('Status update failed:', err);
      setError('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Commit both the outbound action and the inbound promotion of the
  // picked file. Triggered by PickNextFileModal.onSelect once the
  // user has chosen the next file to work on. Routes to
  // pauseAndPromote / sendForSignatureAndPromote based on which
  // action originally opened the modal.
  const handlePickNextFile = async (picked /*, source */) => {
    try {
      setActionLoading(true);
      const currentFirstName = getFirstName(user?.email);
      const actor = { email: user?.email, id: user?.id, firstName: currentFirstName, role };

      let updated;
      if (pendingOutboundAction === 'pause') {
        const result = await pauseAndPromote({
          file,
          reason: pendingPauseReason,
          target: picked,
          actor,
        });
        updated = result.paused;
        setStatusAnim('paused');
      } else {
        // 'send_for_signature'
        const result = await sendForSignatureAndPromote({
          file,
          target: picked,
          actor,
        });
        updated = result.sent;
        setStatusAnim('sent_to_sign');
      }

      setFile(updated);
      setNotesRefresh((n) => n + 1);
      setShowPickNextModal(false);
      setPickNextPausedCandidates([]);
      setPickNextSubmittedCandidates([]);
      setPendingPauseReason('');
      setPendingOutboundAction(null);

      try {
        const refreshed = await getAllFiles();
        setAllFiles(refreshed);
      } catch (e) {
        // Non-critical.
      }

      // Outbound + inbound committed atomically: return to the dashboard
      // with THIS file (the one paused / sent) highlighted (§39).
      const outboundStatus = pendingOutboundAction === 'pause' ? 'paused' : 'sent_to_sign';
      returnToDashboard(navigate, {
        role,
        cardType: 'file',
        cardId: file.id,
        message: movedToMessage(STATUS_LABELS[outboundStatus]),
      });
    } catch (err) {
      console.error('Pick-next-file flow failed:', err);
      setError('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Cancel = true revert of the outbound action. No DB writes have
  // happened yet; closing the modal leaves In Progress occupied with
  // the original file.
  const handleCancelPickNext = () => {
    if (actionLoading) return;
    setShowPickNextModal(false);
    setPickNextPausedCandidates([]);
    setPickNextSubmittedCandidates([]);
    setPendingPauseReason('');
    setPendingOutboundAction(null);
  };

  // Apply a new case structure (advisor only). Performs the orphan
  // screenshot cleanup as part of the same logical operation.
  const handleApplyCaseStructure = async (newCount, newLabels) => {
    try {
      setActionLoading(true);
      await updateCaseStructure({
        fileRowId: file.id,
        newCount,
        newLabels,
      });
      await loadFile();
      setShowEditCasesModal(false);
    } catch (err) {
      console.error('Case structure update failed:', err);
      setError(err.message || 'Failed to update case structure');
    } finally {
      setActionLoading(false);
    }
  };

  // Awaiting Signature → Signed & Sent to NB transition (CLAUDE.md §35).
  // The flow itself — fetch the notepad, generate and download the memo,
  // then ONE markSigned write with the notes lock, with both failure
  // branches — lives in useHandoverTransition (src/hooks, step F3). The
  // hook owns the modal state and calls back here only after the write
  // has succeeded; the view keeps the UI follow-through and the dashboard
  // return.
  const handleSignedTransitionSuccess = async (updated) => {
    // Success — same UI follow-through as handleStatusChange('signed').
    setFile(updated);
    setNotesRefresh((n) => n + 1);
    setStatusAnim('signed');
    try {
      const refreshed = await getAllFiles();
      setAllFiles(refreshed);
    } catch (e) {
      // Non-critical; the dashboard view will refetch on next mount.
    }

    // The memo PDF has been generated AND downloaded (step 1–2) and the
    // status write has succeeded (step 3): only now return to the
    // dashboard (§39). Either failure path above returned early, leaving
    // the modal open with its error.
    returnToDashboard(navigate, {
      role,
      cardType: 'file',
      cardId: file.id,
      message: movedToMessage(STATUS_LABELS.signed),
    });
  };

  const handover = useHandoverTransition({ file, id, user, role, onSuccess: handleSignedTransitionSuccess });

  // Change file priority — adviser only
  const handlePriorityChange = async (newPriority) => {
    try {
      setActionLoading(true);
      const updated = await updateFile( id, { priority: newPriority });
      setFile(updated);

      // Append a system note when the adviser flags the file as Urgent.
      // No note when the urgent flag is removed (per spec). A note-write
      // failure must not break the priority change, so we tolerate it.
      if (newPriority === 'Urgent') {
        try {
          await createSystemNote(
            file.id,
            `File marked as Urgent by ${getFirstName(user?.email)}.`
          );
          setNotesRefresh((n) => n + 1);
        } catch (e) {
          console.warn('Failed to record urgent system note:', e);
        }
      }

      setShowUrgentConfirm(false);
      setUrgentToast(
        newPriority === 'Urgent'
          ? 'File marked as urgent and moved to the top of the queue.'
          : 'File priority returned to normal.'
      );
    } catch (err) {
      console.error('Priority update failed:', err);
      setError('Failed to update priority');
    } finally {
      setActionLoading(false);
    }
  };

  // Delete file — adviser only. For test records the button renders as
  // "Delete Test File" (CLAUDE.md §33); the confirmed hard delete is the
  // documented exception to the never-delete rule, applying to test
  // records only. Real records keep the pre-existing adviser delete
  // behaviour unchanged.
  const handleDeleteFile = async () => {
    // Defensive re-verification for the test-file delete path: the action
    // must only ever execute for the Adviser on a record that is still
    // is_test = true, regardless of UI state.
    if (file.isTest && (role !== 'adviser' || file.isTest !== true)) {
      setShowDeleteConfirm(false);
      return;
    }
    try {
      setDeleting(true);
      // Clean up storage files first
      if (file.fileId) {
        await deleteAllFileStorage(file.fileId);
      }
      // Delete the database record
      await deleteFile( file.id);
      // Redirect to dashboard with success message
      navigate('/', { state: { deleteSuccess: true, deletedName: file.clientName } });
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete file');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Manual attachment purge — adviser only (CLAUDE.md §29). The "escape
  // hatch" for purging working copies at any stage. Server re-verifies the
  // adviser role (UI gating is not trusted) and writes the system note.
  const handleManualPurge = async () => {
    try {
      setPurging(true);
      await manualPurge(file.id, getFirstName(user?.email));
      // Reload so the greyed-out treatment + new system note appear.
      await loadFile();
      setNotesRefresh((n) => n + 1);
      setShowPurgeConfirm(false);
    } catch (err) {
      console.error('Manual purge failed:', err);
      setError(err.message || 'Failed to purge attachments');
    } finally {
      setPurging(false);
    }
  };

  // Reject completion — move file back to signed
  const handleRejectCompletion = async () => {
    if (!rejectionReason.trim()) return;
    setRejecting(true);
    try {
      const reason = rejectionReason.trim();
      const updated = await updateFile( id, {
        status: 'signed',
        completedAt: null,
        qualifiesForBonus: false,
        rejectionReason: reason,
        atworkScreenshotUrl: null,
      });
      setFile(updated);

      // Append a system note to the File Notes log so the rejection
      // appears in the chronological audit trail alongside other events.
      // The notepad's 200-char body cap is enforced at the API/DB layer,
      // so the inline reason must be truncated to fit. The full reason
      // remains intact on files.rejection_reason and in the timeline.
      const firstName = getFirstName(user?.email);
      const prefix = `File rejected by ${firstName}. Reason: `;
      const room = NOTE_BODY_MAX - prefix.length;
      const body = prefix + (reason.length > room
        ? `${reason.slice(0, Math.max(0, room - 1))}…`
        : reason);
      try {
        await createSystemNote(file.id, body);
        setNotesRefresh((n) => n + 1);
      } catch (e) {
        console.warn('Failed to record rejection system note:', e);
      }

      setShowRejectModal(false);
      setRejectionReason('');
      // Completed → Signed & Sent to New Business is a column move: return
      // to the dashboard with the card highlighted in that column (§39).
      returnToDashboard(navigate, {
        role,
        cardType: 'file',
        cardId: file.id,
        message: movedToMessage(STATUS_LABELS.signed),
      });
    } catch (err) {
      console.error('Reject failed:', err);
      setError('Failed to reject completion: ' + err.message);
    } finally {
      setRejecting(false);
    }
  };

  // App-level role gate (CLAUDE.md §30). Client files are openable/readable
  // only by the Adviser and the Administration Assistant. File cards no
  // longer render on the New Business Assistant's dashboard, so this check
  // now only protects deep links / direct URLs — refused with the friendly
  // message rather than a silent redirect. Keyed off the ACTIVE role, so
  // switching roles changes access immediately. App-level workflow gating
  // only — RLS is untouched.
  if (role === 'new_business_assistant') {
    return (
      <div style={styles.errorState}>
        <AlertTriangle size={40} color="#F4A261" />
        <h3>Only the Administration Assistant can open client files.</h3>
        <button className="btn btn-outline" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '2rem' }}>
        <div className="skeleton" style={{ height: '24px', width: '200px', marginBottom: '1rem' }} />
        <div className="skeleton" style={{ height: '300px', borderRadius: '12px' }} />
      </div>
    );
  }

  if (error || !file) {
    return (
      <div style={styles.errorState}>
        <AlertTriangle size={40} color="#C0392B" />
        <h3>{error || 'File not found'}</h3>
        <button className="btn btn-outline" onClick={() => navigate('/')}>Back to Dashboard</button>
      </div>
    );
  }

  const urgency = sla.urgency;
  const activeDays = sla.activeDays;
  const slaColors = { green: '#2EC4B6', amber: '#F4A261', red: '#C0392B' };

  // Seal the file's contents (brief, voice, attachments, notes) from anyone
  // viewing in the assistant role until they draw the file and start the
  // SLA clock. Adviser view is always unlocked. The rule itself lives in the
  // shared Inbox draw gate helper (CLAUDE.md §40), which preparation
  // requests mirror for the New Business Assistant.
  const isLockedForAssistant = isSealedInInbox('file', file.status, role);

  return (
    <div>
      {/* Back button + header */}
      <div style={styles.header}>
        <button className="btn btn-outline" onClick={() => navigate(-1)} style={styles.backBtn}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={styles.headerRight}>
          <span className={`badge status-${file.status}`} style={styles.statusBadge}>
            {STATUS_LABELS[file.status]}
          </span>
          {/* TEST badge — test records only (CLAUDE.md §33). Existing
              design tokens only. */}
          {file.isTest && (
            <span
              className="badge"
              style={{
                background: 'var(--cp-surface-alt)',
                border: '1px dashed var(--cp-warning)',
                color: 'var(--cp-warning)',
              }}
            >
              TEST
            </span>
          )}
          {file.priority === 'Urgent' && (
            <span className="badge badge-urgent">Urgent</span>
          )}
        </div>
      </div>

      <div className="layout-grid layout-grid--narrow-sidebar" style={styles.grid}>
        {/* Main content */}
        <motion.div
          className="card"
          style={styles.mainCard}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 style={styles.clientName}>{file.clientName}</h2>

          {/* Meta info */}
          <div style={styles.metaGrid}>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Work Origin</span>
              <span style={styles.metaValue}>{file.workCategory || '—'}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Consultation</span>
              <span style={styles.metaValue}>{formatDate(file.appointmentDate)}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Submitted</span>
              <span style={styles.metaValue}>{formatDateTime(file.submittedAt)}</span>
            </div>
            <div style={styles.metaItem}>
              <span style={styles.metaLabel}>Drawn</span>
              <span style={styles.metaValue}>{file.drawnAt ? formatDateTime(file.drawnAt) : '—'}</span>
            </div>
          </div>

          {/* Adviser commission split — sits next to the origination
              metadata so it reads as part of the file's "who/where/how
              much" header. Editable while the file is in Inbox / In
              Progress / Paused; sealed (read-only with a small lock
              icon) from Awaiting Signature onwards. */}
          <CommissionSplitsCard
            file={file}
            id={id}
            actionLoading={actionLoading}
            setActionLoading={setActionLoading}
            setFile={setFile}
            setError={setError}
          />

          {/* Screenshot upload warning */}
          {screenshotWarning && (
            <motion.div
              style={styles.warningBox}
              initial={{ opacity: 0, y: -10 }}
              animate={screenshotWarningFading ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
              transition={{ duration: screenshotWarningFading ? 0.5 : 0.3 }}
            >
              <AlertTriangle size={16} color="#E76F51" />
              <span>{screenshotWarning}</span>
            </motion.div>
          )}

          {/* Urgent priority toast */}
          {urgentToast && (
            <motion.div
              style={styles.urgentToastBox}
              initial={{ opacity: 0, y: -10 }}
              animate={urgentToastFading ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
              transition={{ duration: urgentToastFading ? 0.5 : 0.3 }}
            >
              <CheckCircle2 size={16} color="#2EC4B6" />
              <span>{urgentToast}</span>
            </motion.div>
          )}

          {/* Reject completion toast */}
          {rejectToast && (
            <motion.div
              style={styles.rejectToastBox}
              initial={{ opacity: 0, y: -10 }}
              animate={rejectToastFading ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
              transition={{ duration: rejectToastFading ? 0.5 : 0.3 }}
            >
              <AlertTriangle size={16} color="#E76F51" />
              <span>{rejectToast}</span>
            </motion.div>
          )}

          {/* Concurrency warning */}
          {concurrencyWarning && (
            <motion.div
              style={styles.warningBox}
              initial={{ opacity: 0, y: -10 }}
              animate={warningFading ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
              transition={{ duration: warningFading ? 0.5 : 0.3 }}
            >
              <AlertTriangle size={16} color="#F4A261" />
              <span>{concurrencyWarning}</span>
            </motion.div>
          )}

          {/* Rejection reason warning — always shown, since it only appears
              after the file has been drawn and returned for correction. */}
          {file.rejectionReason && (
            <div style={styles.rejectionPanel}>
              <div style={styles.rejectionHeader}>
                <AlertTriangle size={18} color="#E76F51" />
                <h3 style={styles.rejectionTitle}>Returned for Correction</h3>
              </div>
              <p style={styles.rejectionText}>{file.rejectionReason}</p>
            </div>
          )}

          {isLockedForAssistant ? (
            <div style={styles.section}>
              <div style={styles.lockedCard}>
                <div style={styles.lockedIconWrap}>
                  <Lock size={22} color="#0F4C75" />
                </div>
                <h3 style={styles.lockedTitle}>File sealed until drawn</h3>
                <p style={styles.lockedBody}>
                  Marinus's brief, voice note, attachments and notes will unlock
                  the moment you click <strong>Draw File</strong>. Once drawn,
                  the file is yours and the SLA clock starts.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Voice Note Player */}
              {file.voiceNoteUrl && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Voice Note</h3>
                  <audio
                    controls
                    src={file.voiceNoteUrl}
                    style={styles.audioPlayer}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {/* Summary — the adviser's Client File Brief
                  (files.written_instructions), written on the submission
                  form. Read-only here; line breaks preserved. The legacy
                  ai_summary / transcription columns are deprecated and are
                  no longer displayed (29 September 2026). */}
              {file.writtenInstructions && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Summary</h3>
                  <div style={styles.summaryBox}>
                    <pre style={styles.summaryText}>{file.writtenInstructions}</pre>
                  </div>
                </div>
              )}

              {/* Notes for New Business — the Administration Assistant's
                  handover message, saved and locked when the handover memo
                  was generated (migration 27). Read-only for every role;
                  rendered only when a value exists. Not a file note. */}
              {file.handoverNotes && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Notes for New Business</h3>
                  <div style={styles.summaryBox}>
                    <pre style={styles.summaryText}>{file.handoverNotes}</pre>
                    {file.handoverNotesLockedAt && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--inbox-text-muted)' }}>
                        Locked {formatDateTime(file.handoverNotesLockedAt)} — printed on the handover memo. Later communication goes in the notepad below.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Verification system diagnostic banner — advisor only.
                  Renders when an Anthropic call for this file failed in
                  the last 48 hours and the advisor hasn't dismissed the
                  banner this session. Does NOT use the same amber as
                  Layer 3 flags so the visual signal stays distinct
                  ("system-level problem" vs "case needs review"). */}
              {role === 'adviser' && screenshots.verificationDiagnostic && !screenshots.diagnosticDismissed && (
                <div style={styles.diagnosticBanner}>
                  <div style={styles.diagnosticHeader}>
                    <AlertOctagon size={18} color="#8C5A2D" />
                    <strong>Verification system reported an error</strong>
                    <button
                      type="button"
                      style={styles.diagnosticDismiss}
                      onClick={screenshots.dismissDiagnostic}
                      aria-label="Dismiss diagnostic banner"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <div style={styles.diagnosticBody}>
                    <div>
                      Most recent failure:{' '}
                      <code style={styles.diagnosticCode}>
                        {screenshots.verificationDiagnostic.errorStatus ?? 'network'}
                      </code>{' '}
                      — {screenshots.verificationDiagnostic.errorMessage || '(no message)'}
                    </div>
                    <div>
                      Model attempted:{' '}
                      <code style={styles.diagnosticCode}>{screenshots.verificationDiagnostic.modelUsed || '—'}</code>
                    </div>
                    <div style={styles.diagnosticMeta}>
                      Time: {formatDateTime(screenshots.verificationDiagnostic.attemptedAt)} ({timeAgo(screenshots.verificationDiagnostic.attemptedAt)})
                    </div>
                    <div style={styles.diagnosticMeta}>
                      Common causes: deprecated model (update <code style={styles.diagnosticCode}>VERIFICATION_MODEL</code> env var), API key issue, or Anthropic outage.
                    </div>
                    <button
                      type="button"
                      style={styles.diagnosticLink}
                      onClick={screenshots.openAggregateDiagnostics}
                    >
                      View recent verification errors
                    </button>
                  </div>
                </div>
              )}

              {/* Cases — read-only description list for the assistant.
                  Hidden on legacy files (caseCount is null). Adviser
                  sees an "Edit cases" affordance to the right of the
                  heading. The descriptions are written verbatim onto
                  the new business handover memo PDF, so we render
                  line breaks the adviser typed faithfully here too. */}
              {screenshots.hasCases && (
                <div style={styles.section}>
                  <div style={styles.casesHeader}>
                    <h3 style={styles.sectionTitle}>
                      <Layers size={14} style={{ verticalAlign: '-2px', marginRight: '0.35rem', color: 'var(--cp-secondary)' }} />
                      Cases ({file.caseCount})
                    </h3>
                    {role === 'adviser' && file.status !== 'completed' && (
                      <button
                        type="button"
                        style={styles.editCasesBtn}
                        onClick={() => setShowEditCasesModal(true)}
                        disabled={actionLoading}
                      >
                        <Pencil size={12} /> Edit cases
                      </button>
                    )}
                  </div>
                  <ol style={styles.caseList}>
                    {(file.caseLabels || []).map((label, idx) => (
                      <li key={idx} style={{ ...styles.caseListItem, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                        <span style={styles.caseListTag}>Case {idx + 1}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ ...styles.caseListLabel, whiteSpace: 'pre-wrap' }}>{label}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* File Notes — append-only progress log */}
              <FileNotes
                fileId={file.id}
                currentUser={{
                  id: user?.id || null,
                  displayName: getFirstName(user?.email),
                }}
                currentRole={role}
                refreshSignal={notesRefresh}
              />

              {/* Notes */}
              {file.notes && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Additional Notes</h3>
                  <p style={styles.notesText}>{file.notes}</p>
                </div>
              )}

              {/* Attachments. Once purged (CLAUDE.md §29) the storage objects
                  are gone — we keep the historical list but render each entry
                  inert (greyed, no link) so there are no broken signed-URL
                  links, with a "Purged DD/MM/YYYY" label. */}
              {file.attachments && file.attachments.length > 0 && (
                <AttachmentsCard file={file} />
              )}
            </>
          )}

          {/* AtWork Confirmation Screenshot — legacy single-screenshot files. */}
          {!screenshots.hasCases && file.atworkScreenshotUrl && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>AtWork Confirmation Screenshot</h3>
              <a href={file.atworkScreenshotUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={file.atworkScreenshotUrl}
                  alt="AtWork confirmation screenshot"
                  style={styles.screenshotThumbnail}
                />
              </a>
            </div>
          )}

          {/* AtWork Confirmation Screenshots — multi-case files in a
              completed/post-signed view. The active upload UX lives in the
              sidebar; this is the read-only audit-trail rendering. */}
          {screenshots.hasCases && screenshots.caseScreenshots.length > 0 && (
            <CaseScreenshotsSection
              file={file}
              screenshotsByIndex={screenshots.screenshotsByIndex}
              onOpenLightbox={screenshots.openLightbox}
            />
          )}
        </motion.div>

        {/* Sidebar — SLA & Actions */}
        <div style={styles.sidebar}>
          {/* SLA Timer — never shown for test records: the bonus/SLA clock
              does not apply to them at all (CLAUDE.md §33). */}
          {!file.isTest && file.drawnAt && !['completed'].includes(file.status) && (
            <div className="card" style={styles.slaCard}>
              <div style={styles.slaHeader}>
                <Clock size={16} color={slaColors[urgency]} />
                <span style={{ ...styles.slaTitle, color: slaColors[urgency] }}>SLA Timer</span>
              </div>
              <div style={{ ...styles.slaDays, color: slaColors[urgency] }}>
                {activeDays.toFixed(1)}
              </div>
              <div style={styles.slaLabel}>active days of 3.0</div>
              <div style={styles.slaBar}>
                <div style={{
                  ...styles.slaBarFill,
                  width: `${Math.min(100, (activeDays / 3) * 100)}%`,
                  background: slaColors[urgency],
                }} />
              </div>
              {file.status === 'paused' && (
                <div style={styles.pausedNote}>
                  <Pause size={12} /> Clock paused — see File Notes for the reason.
                </div>
              )}
              {/* Motivational message based on SLA status */}
              {(() => {
                const msg = getSLAMotivationalMessage(file);
                return msg ? (
                  <p style={styles.slaMotivation}>{msg}</p>
                ) : null;
              })()}
            </div>
          )}

          {/* Action Buttons — the Actions card for every role and status
              (components/files/FileActionsCard.jsx, step F5a). Every handler
              it calls still lives here and is passed in. */}
          <FileActionsCard
            file={file}
            role={role}
            allFiles={allFiles}
            concurrency={concurrency}
            actionLoading={actionLoading}
            handleStatusChange={handleStatusChange}
            setShowPauseModal={setShowPauseModal}
            handover={handover}
            screenshots={screenshots}
            highlightUpload={highlightUpload}
            setScreenshotWarning={setScreenshotWarning}
            setHighlightUpload={setHighlightUpload}
            handlePriorityChange={handlePriorityChange}
            setShowUrgentConfirm={setShowUrgentConfirm}
            purging={purging}
            setShowPurgeConfirm={setShowPurgeConfirm}
            deleting={deleting}
            setShowDeleteConfirm={setShowDeleteConfirm}
            setShowRejectModal={setShowRejectModal}
          />

          {/* WhatsApp Message — shown when file is awaiting signature. Never
              offered for test records: no client-facing notification content
              is generated for them (CLAUDE.md §33). */}
          {file.status === 'sent_to_sign' && !file.isTest && (
            <WhatsAppPanel clientName={file.clientName} language={file.clientLanguage} />
          )}

          {/* Timeline */}
          <div className="card" style={styles.timelineCard}>
            <h3 style={styles.actionsTitle}>Timeline</h3>
            <div style={styles.timeline}>
              <TimelineItem label="Submitted" date={file.submittedAt} />
              <TimelineItem label="Drawn" date={file.drawnAt} />
              {file.pausedAt && <TimelineItem label="Paused" date={file.pausedAt} />}
              {file.resumedAt && <TimelineItem label="Resumed" date={file.resumedAt} />}
              <TimelineItem label="Awaiting Signature" date={file.sentToSignAt} />
              <TimelineItem label="Signed & Sent to New Business" date={file.signedAt} />
              {file.completedAt && <TimelineItem label="Completed" date={file.completedAt} />}
              {file.rejectionReason && (
                <div style={styles.timelineItem}>
                  <div style={{ ...styles.timelineDot, background: 'var(--cp-warning)' }} />
                  <div>
                    <div style={{ ...styles.timelineLabel, color: 'var(--cp-warning)' }}>
                      Rejected by Adviser — {file.rejectionReason}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* First file of the month celebration video */}
      {showFirstFileVideo && (
        <VideoInterstitial
          videoUrl={FIRST_FILE_VIDEO_URL}
          onComplete={() => {
            // Fires on natural completion AND on the error path (the
            // interstitial's "Continue to dashboard" button), so the
            // deferred return below always runs — a failed video never
            // strands the page (§39).
            setShowFirstFileVideo(false);
            const pending = pendingReturnRef.current;
            if (pending) {
              pendingReturnRef.current = null;
              returnToDashboard(navigate, pending);
            }
          }}
        />
      )}

      {/* Status Change Animation */}
      <StatusChangeAnimation
        show={!!statusAnim}
        status={statusAnim}
        onComplete={() => setStatusAnim(null)}
      />

      {/* Pause reason modal — reason is recorded as a note in the File Notes log. */}
      {showPauseModal && (() => {
        const len = pauseReason.length;
        const overLimit = len > NOTE_BODY_MAX;
        const counterColour =
          len >= NOTE_BODY_MAX - 5 ? 'var(--cp-danger)'
          : len >= NOTE_BODY_MAX - 10 ? 'var(--cp-accent)'
          : 'var(--cp-text-muted)';
        return (
          <div style={styles.modalOverlay} onClick={() => setShowPauseModal(false)}>
            <motion.div
              className="card"
              style={styles.modal}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <h3 style={{ marginBottom: '0.75rem' }}>Pause File</h3>
              <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                Why is this file being paused? The SLA clock will be stopped and the reason will be recorded in File Notes.
              </p>
              <textarea
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
                placeholder="e.g. Waiting for client to provide ID document"
                rows={3}
                maxLength={NOTE_BODY_MAX + 20}
                style={{ marginBottom: '0.4rem' }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '0.75rem',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: counterColour,
              }}>
                {len}/{NOTE_BODY_MAX}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-outline" onClick={() => setShowPauseModal(false)}>Cancel</button>
                <button
                  className="btn btn-accent"
                  onClick={() => handleStatusChange('paused')}
                  disabled={!pauseReason.trim() || overLimit}
                >
                  Pause File
                </button>
              </div>
            </motion.div>
          </div>
        );
      })()}

      {/* Pick-next-file modal — opens when pausing or sending for
          signature would leave In Progress empty while other paused
          files exist. The original outbound action has NOT been
          committed yet at this point; cancelling closes the modal
          with no DB writes so In Progress stays occupied with the
          original file (see CLAUDE.md §7.6). */}
      <PickNextFileModal
        open={showPickNextModal}
        pausedCandidates={pickNextPausedCandidates}
        submittedCandidates={pickNextSubmittedCandidates}
        outgoingFile={file}
        outgoingAction={pendingOutboundAction || 'pause'}
        busy={actionLoading}
        onCancel={handleCancelPickNext}
        onSelect={handlePickNextFile}
      />

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <DeleteFileModal file={file} deleting={deleting} setShowDeleteConfirm={setShowDeleteConfirm} handleDeleteFile={handleDeleteFile} />
      )}

      {/* Purge attachments confirmation modal (CLAUDE.md §29) */}
      {showPurgeConfirm && (
        <PurgeConfirmModal file={file} purging={purging} setShowPurgeConfirm={setShowPurgeConfirm} handleManualPurge={handleManualPurge} />
      )}

      {/* Reject completion modal */}
      {showRejectModal && (
        <div style={styles.modalOverlay} onClick={() => !rejecting && setShowRejectModal(false)}>
          <motion.div
            className="card"
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--cp-warning)' }}>Reject Completion</h3>
            <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
              This will move the file back to the previous stage. Please provide a reason.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Reason for rejection..."
              rows={3}
              style={{
                width: '100%',
                padding: '0.6rem',
                borderRadius: '8px',
                border: '1px solid var(--cp-border)',
                fontSize: '0.85rem',
                marginBottom: '1rem',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-outline"
                onClick={() => setShowRejectModal(false)}
                disabled={rejecting}
              >
                Cancel
              </button>
              <button
                style={styles.confirmRejectBtn}
                onClick={handleRejectCompletion}
                disabled={!rejectionReason.trim() || rejecting}
              >
                {rejecting ? 'Rejecting…' : 'Confirm Rejection'}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Lightbox for full-screen screenshot view (multi-case + click-throughs). */}
      {screenshots.lightboxUrl && (
        <div
          style={styles.modalOverlay}
          onClick={screenshots.closeLightbox}
          role="presentation"
        >
          <img
            src={screenshots.lightboxUrl}
            alt="AtWork confirmation screenshot"
            style={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Awaiting Signature → Signed & Sent to NB transition modal.
          Generates the new business handover memo PDF for Vicky and
          flips the status only on a successful PDF generation. */}
      {handover.open && (
        <StatusTransitionToNbModal
          file={file}
          role={role}
          commissionSplits={file.commissionSplits || defaultCommissionSplits()}
          cases={(file.caseLabels || []).map((s) => (typeof s === 'string' ? s : ''))}
          busy={handover.busy}
          errorMessage={handover.error}
          onCancel={handover.cancel}
          onConfirm={handover.confirm}
        />
      )}

      {/* Edit case count modal — adviser only. Includes a confirmation
          step when reducing the count would orphan uploaded screenshots. */}
      {showEditCasesModal && (
        <EditCasesModal
          file={file}
          screenshotsByIndex={screenshots.screenshotsByIndex}
          actionLoading={actionLoading}
          onClose={() => setShowEditCasesModal(false)}
          onApply={handleApplyCaseStructure}
        />
      )}

      {/* Layer 2 override modal — advisor confirms uploading a screenshot
          that AI verification rejected, with a non-empty reason. */}
      {screenshots.overrideModal && (
        <OverrideUploadModal
          caseIndex={screenshots.overrideModal.caseIndex}
          caseLabel={(file.caseLabels || [])[screenshots.overrideModal.caseIndex] || ''}
          rowError={screenshots.caseRowErrors[screenshots.overrideModal.caseIndex] || null}
          actionLoading={screenshots.caseUploadingIndex === screenshots.overrideModal.caseIndex}
          onCancel={screenshots.closeOverride}
          onConfirm={(reason) => screenshots.handleOverrideUpload(screenshots.overrideModal.caseIndex, reason)}
        />
      )}

      {/* Layer 3 flag detail modal — both roles. Adviser sees a
          "Mark as verified" footer to clear the flag with a reason. */}
      {screenshots.flagModalCase && (
        <FlagDetailModal
          shot={screenshots.flagModalCase}
          caseLabel={(file.caseLabels || [])[screenshots.flagModalCase.caseIndex] || ''}
          isAdviser={role === 'adviser'}
          actionLoading={actionLoading}
          onClose={screenshots.closeFlagDetails}
          onClearFlag={(reason) => screenshots.handleClearFlag(screenshots.flagModalCase.caseIndex, reason)}
        />
      )}

      {/* Aggregate verification diagnostics modal — advisor only.
          Lists the most recent system events across all files. */}
      {screenshots.showAggregateDiagnostics && (
        <AggregateDiagnosticsModal onClose={screenshots.closeAggregateDiagnostics} />
      )}

      {/* Urgent confirmation modal */}
      {showUrgentConfirm && (
        <div style={styles.modalOverlay} onClick={() => setShowUrgentConfirm(false)}>
          <motion.div
            className="card"
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 style={{ marginBottom: '0.75rem', color: 'var(--cp-accent)' }}>Mark as Urgent</h3>
            <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
              Mark this file as urgent? It will move to the top of the queue.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-outline"
                onClick={() => setShowUrgentConfirm(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                style={styles.confirmUrgentBtn}
                onClick={() => handlePriorityChange('Urgent')}
                disabled={actionLoading}
              >
                {actionLoading ? 'Updating…' : 'Mark as Urgent'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  backBtn: {
    fontSize: '0.85rem',
  },
  headerRight: {
    display: 'flex',
    gap: '0.5rem',
  },
  statusBadge: {
    fontSize: '0.8rem',
    padding: '0.3rem 0.8rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 340px',
    gap: '1.5rem',
    alignItems: 'start',
  },
  mainCard: {
    padding: '2rem',
  },
  clientName: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
    marginBottom: '1rem',
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.5rem',
    padding: '1rem',
    background: 'var(--cp-bg)',
    borderRadius: '10px',
  },
  metaItem: {},
  metaLabel: sharedStyles.metaLabel,
  metaValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--cp-text)',
  },
  section: sharedStyles.section,
  sectionTitle: sharedStyles.sectionTitle,
  summaryBox: {
    background: 'linear-gradient(135deg, rgba(15, 76, 117, 0.04), rgba(27, 154, 170, 0.06))',
    border: '1px solid rgba(27, 154, 170, 0.15)',
    borderRadius: '10px',
    padding: '1rem',
  },
  summaryText: {
    fontSize: '0.85rem',
    lineHeight: 1.6,
    color: 'var(--cp-text)',
    whiteSpace: 'pre-wrap',
    fontFamily: "'DM Sans', sans-serif",
    margin: 0,
  },
  notesText: {
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    lineHeight: 1.6,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  slaCard: {
    textAlign: 'center',
    padding: '1.25rem',
  },
  slaHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    marginBottom: '0.5rem',
  },
  slaTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  slaDays: {
    fontSize: '2.5rem',
    fontWeight: 700,
    lineHeight: 1.1,
  },
  slaLabel: {
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    marginBottom: '0.5rem',
  },
  slaBar: {
    height: '6px',
    background: 'var(--cp-surface-alt)',
    borderRadius: '999px',
    overflow: 'hidden',
  },
  slaBarFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.5s ease',
  },
  pausedNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.3rem',
    marginTop: '0.5rem',
    fontSize: '0.75rem',
    color: 'var(--cp-accent)',
  },
  slaMotivation: {
    fontSize: '0.75rem',
    color: 'var(--cp-text-muted)',
    textAlign: 'center',
    marginTop: '0.5rem',
    fontStyle: 'italic',
    lineHeight: 1.4,
  },
  actionsTitle: sharedStyles.actionsTitle,
  timelineCard: {
    padding: '1.25rem',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  timelineItem: timelineStyles.timelineItem,
  timelineDot: timelineStyles.timelineDot,
  timelineLabel: timelineStyles.timelineLabel,
  warningBox: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem 1.5rem',
    background: 'var(--cp-surface)',
    border: '1px solid rgba(244, 162, 97, 0.4)',
    borderRadius: '12px',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    maxWidth: '400px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    cursor: 'default',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  audioPlayer: {
    width: '100%',
    borderRadius: '8px',
  },
  errorState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '4rem',
    gap: '1rem',
  },
  modalOverlay: caseModalStyles.modalOverlay,
  modal: caseModalStyles.modal,
  confirmRejectBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0.65rem 1.25rem',
    background: 'var(--cp-warning)',
    color: 'var(--cp-surface)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  screenshotThumbnail: {
    maxWidth: '100%',
    maxHeight: '300px',
    borderRadius: '8px',
    border: '1px solid var(--cp-border)',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  casesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  editCasesBtn: sharedStyles.editCasesBtn,
  caseList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  caseListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    padding: '0.5rem 0.75rem',
    background: 'var(--cp-bg)',
    border: '1px solid var(--cp-surface-alt)',
    borderRadius: '8px',
  },
  caseListTag: caseModalStyles.caseListTag,
  caseListLabel: {
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    fontWeight: 500,
  },
  lightboxImg: {
    maxWidth: '92vw',
    maxHeight: '92vh',
    borderRadius: '8px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.35)',
    cursor: 'default',
  },
  diagnosticBanner: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    padding: '0.85rem 1rem',
    background: 'rgba(140, 90, 45, 0.06)',
    border: '1px solid rgba(140, 90, 45, 0.3)',
    borderRadius: '10px',
    marginBottom: '1rem',
    fontSize: '0.82rem',
    color: 'var(--cp-text)',
  },
  diagnosticHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#8C5A2D',
  },
  diagnosticDismiss: {
    marginLeft: 'auto',
    background: 'transparent',
    border: 'none',
    color: 'var(--cp-text-muted)',
    cursor: 'pointer',
    padding: '0.15rem',
    display: 'flex',
  },
  diagnosticBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    paddingLeft: '1.6rem',
  },
  diagnosticCode: {
    background: 'rgba(140, 90, 45, 0.1)',
    padding: '0.05rem 0.35rem',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.78rem',
  },
  diagnosticMeta: {
    color: 'var(--cp-text-muted)',
    fontSize: '0.78rem',
  },
  diagnosticLink: {
    alignSelf: 'flex-start',
    background: 'transparent',
    border: 'none',
    padding: 0,
    color: 'var(--cp-primary)',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
    textDecoration: 'underline',
    marginTop: '0.25rem',
  },
  rejectionPanel: {
    background: 'linear-gradient(135deg, rgba(231, 111, 81, 0.08), rgba(244, 162, 97, 0.08))',
    border: '1px solid rgba(231, 111, 81, 0.3)',
    borderRadius: '10px',
    padding: '1rem 1.25rem',
    marginBottom: '1.25rem',
  },
  lockedCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '2rem 1.5rem',
    background: 'linear-gradient(135deg, rgba(15, 76, 117, 0.05), rgba(27, 154, 170, 0.05))',
    border: '1px dashed rgba(15, 76, 117, 0.25)',
    borderRadius: '12px',
  },
  lockedIconWrap: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    background: 'rgba(15, 76, 117, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '0.75rem',
  },
  lockedTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: 'var(--cp-primary)',
    margin: '0 0 0.5rem 0',
  },
  lockedBody: {
    fontSize: '0.9rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.5,
    margin: 0,
    maxWidth: '420px',
  },
  rejectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
  },
  rejectionTitle: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: 'var(--cp-warning)',
    margin: 0,
  },
  rejectionText: {
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    lineHeight: 1.5,
    margin: 0,
    paddingLeft: '1.6rem',
  },
  rejectToastBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    background: 'rgba(231, 111, 81, 0.1)',
    border: '1px solid rgba(231, 111, 81, 0.3)',
    borderRadius: '8px',
    color: 'var(--cp-warning)',
    fontSize: '0.85rem',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  confirmUrgentBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0.65rem 1.25rem',
    background: 'var(--cp-accent)',
    color: 'var(--cp-surface)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  urgentToastBox: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem 1.5rem',
    background: 'var(--cp-surface)',
    border: '1px solid rgba(46, 196, 182, 0.4)',
    borderRadius: '12px',
    fontSize: '0.85rem',
    color: 'var(--cp-text)',
    maxWidth: '400px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    cursor: 'default',
    userSelect: 'none',
    pointerEvents: 'none',
  },
};
